import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';

type EditableState<T, V = T> = {
  value: V;
  setValue: Dispatch<SetStateAction<V>>;
  isDirty: boolean;
  reset: (newData: T) => void;
};

/**
 * Enhanced useState hook, with dirty tracking and reset.
 * Useful in conjuction with API mutations, value can be sent to the API directly,
 * reset can be called with new data from the API response.
 *
 * @deprecated Use `useEditableStateUpdates` instead for control over updated fields.
 */
export function useEditableState<T, V = T>(data: T, transform?: (v: T) => V): EditableState<T, V> {
  const convert = useCallback(
    (v: T) => {
      if (transform) {
        return transform(v);
      }
      return v as unknown as V;
    },
    [transform]
  );

  const [initial, setInitial] = useState<V>(convert(data));
  const [value, setValue] = useState<V>(initial);

  const isDirty = useMemo(() => !isEqual(initial, value), [initial, value]);

  const reset = useCallback(
    (newData: T) => {
      const v = convert(newData);
      setInitial(v);
      setValue(v);
    },
    [convert]
  );

  return { value, setValue, isDirty, reset };
}

type EditableStateUpdates<T, V = T> = {
  value: V;
  updates: Partial<V>;
  setUpdates: (updates: Partial<V>) => void;
  isDirty: boolean;
  reset: (newData: T) => void;
};

/**
 * Enhanced useState hook, with dirty tracking and reset.
 * Keeps track of updates separately from the initial data.
 * Updates can be used to only send the fields that have changed to the API.
 * Value provides a merged view of the initial data and the updates.
 */
export function useEditableStateUpdates<T, V = T>(data: T, transform?: (v: T) => V): EditableStateUpdates<T, V> {
  const convert = useCallback(
    (v: T) => {
      if (transform) {
        return transform(v);
      }
      return v as unknown as V;
    },
    [transform]
  );

  const [initial, setInitial] = useState<V>(convert(data));
  const [updates, setUpdates] = useState<Partial<V>>({});
  const value = useMemo<V>(() => {
    return { ...initial, ...updates };
  }, [initial, updates]);

  const isDirty = useMemo(() => !isEqual(initial, value), [initial, value]);

  const reset = useCallback(
    (newData: T) => {
      const v = convert(newData);
      setInitial(v);
      setUpdates({});
    },
    [convert]
  );

  const setPartialUpdates = useCallback((updates: Partial<V>) => {
    setUpdates((prev) => ({ ...prev, ...updates }));
  }, []);

  return { value, updates, setUpdates: setPartialUpdates, isDirty, reset };
}
